import React, { useCallback, useEffect, useMemo } from 'react';
import { toString } from 'lodash';
import { Securities } from 'api';
import { UNISSUED_OPTIONS } from 'common/constants/securityTypes';
import { useStore } from 'common/store';
import { SelectValueViewer } from 'components';
import { OptionType } from 'components/CustomSelect/types';
import { useFundOwnershipContext } from 'context/FundOwnershipContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { RelatedEntity } from '../../../../ValuationsAllocation/types';
import { FundOwnershipTableAlias } from '../../utilities/rowConfig';

const SecurityListValueViewer = (props: { cell: ICell }) => {
  const [{ captableInfo, companyInfo }] = useStore();
  const { columnLegend } = props.cell;
  const { capTableVersionsByCompany, securityListValues, setSecurityListValues } = useFundOwnershipContext();
  const entityCell: ICell = Object.values(props.cell.sheet?.cells ?? {}).find(
    c => c.alias === FundOwnershipTableAlias.ENTITY_REFERENCE && c.columnLegend === columnLegend
  ) as ICell;

  const entityName = useMemo(() => {
    if (!entityCell.value) {
      return '';
    }

    const entityId = entityCell.value;
    const entityIsParentCompany = entityCell.value === companyInfo.id;

    const nameForEntity = entityIsParentCompany
      ? companyInfo.name
      : companyInfo.child_companies.find((c: RelatedEntity) => +c.company_id === +entityId)?.company_name;

    return nameForEntity;
  }, [entityCell.value, companyInfo]);

  const updateSecurityList = useCallback(() => {
    let securities = (captableInfo?.securities as Securities[]) || [];

    const capTableVersionsForSelectedEntity = capTableVersionsByCompany[entityName];

    // the security list is the cap table versions for the entity for the primary cap table
    if (capTableVersionsForSelectedEntity) {
      const primaryCapTableVersion = capTableVersionsForSelectedEntity.find(i => i.is_primary);
      securities = primaryCapTableVersion?.securities || [];
    }

    securities = securities.filter(s => toString(s.security_type) !== toString(UNISSUED_OPTIONS));

    const securityOptions: OptionType[] = securities
      .filter(i => i.id)
      .map(s => ({
        label: s.name,
        value: s.id!.toString(),
        id: s.id!.toString(),
      }));

    setSecurityListValues(securityOptions, columnLegend!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    captableInfo,
    companyInfo,
    entityCell,
    entityName,
    capTableVersionsByCompany,
    setSecurityListValues,
    columnLegend,
  ]);

  useEffect(() => {
    updateSecurityList();
  }, [entityCell?.value, updateSecurityList]);
  return (
    <SelectValueViewer {...props} cell={props.cell} options={securityListValues[columnLegend!]} useAliasList={false} />
  );
};

export default SecurityListValueViewer;
